import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { timezoneService } from '../services/timezoneService';
import MultiSelect from '../components/MultiSelect';
import { useUser } from '../contexts/UserContext';
import {
  TECHNICIAN_VIEW_TICKET_FILTERS_STORAGE_KEY,
  loadTicketListFilters,
  saveTicketListFilters,
} from '../utils/ticketListFilters';

const TechnicianView = () => {
  const { isAdmin } = useUser();
  const initialFilters = loadTicketListFilters(
    TECHNICIAN_VIEW_TICKET_FILTERS_STORAGE_KEY,
    { includeTeamId: true }
  );
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formattedDates, setFormattedDates] = useState({});
  const [statusFilters, setStatusFilters] = useState(initialFilters.status);
  const [categoryFilters, setCategoryFilters] = useState(initialFilters.category);
  const [creatorFilters, setCreatorFilters] = useState(initialFilters.creator);
  const [technicianFilters, setTechnicianFilters] = useState(initialFilters.technician);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(initialFilters.teamId);

  useEffect(() => {
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveTicketListFilters(TECHNICIAN_VIEW_TICKET_FILTERS_STORAGE_KEY, {
      status: statusFilters,
      category: categoryFilters,
      creator: creatorFilters,
      technician: technicianFilters,
      teamId: selectedTeamId,
    });
  }, [statusFilters, categoryFilters, creatorFilters, technicianFilters, selectedTeamId]);

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId]);

  const loadTeams = async () => {
    try {
      const userId = isAdmin() ? null : authService.getUserId();
      const teamsData = await apiService.getTeams(userId);
      setTeams(teamsData);

      if (!isAdmin() && teamsData.length > 0 && selectedTeamId === null) {
        setSelectedTeamId(teamsData[0].id);
      }
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      // For now, load all tickets. In a real app, you'd filter by assigned technician
      const [ticketsData, categoriesData, usersData] = await Promise.all([
        apiService.getTickets(null, null, selectedTeamId),
        apiService.getCategories(),
        apiService.getUsers()
      ]);
      setTickets(ticketsData);
      setCategories(categoriesData);
      setUsers(usersData);
      
      // Format dates with timezone
      const formatted = {};
      for (const ticket of ticketsData) {
        formatted[ticket.id] = await timezoneService.formatDateTime(ticket.createdAt);
      }
      setFormattedDates(formatted);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 1: return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 2: return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 3: return <ClockIcon className="h-4 w-4 text-orange-500" />;
      case 4: return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 5: return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'New';
      case 2: return 'In Progress';
      case 3: return 'Waiting Feedback';
      case 4: return 'Closed';
      case 5: return 'Reopened';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 3: return 'bg-yellow-100 text-yellow-800';
      case 4: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      case 4: return 'Critical';
      default: return 'Unknown';
    }
  };

  // Status options for multi-select
  const statusOptions = [
    { value: 1, label: 'New' },
    { value: 2, label: 'In Progress' },
    { value: 3, label: 'Waiting Feedback' },
    { value: 4, label: 'Closed' },
    { value: 5, label: 'Reopened' }
  ];

  // Category options for multi-select
  const categoryOptions = categories.map(category => ({
    value: category.id,
    label: category.name
  }));

  // Creator options for multi-select (users who created tickets)
  const creatorOptions = users.map(user => ({
    value: user.id,
    label: user.name
  }));

  // Technician options for multi-select (technicians and admins)
  const technicianOptions = users
    .filter(user => user.role === 2 || user.role === 3) // Technician or Admin
    .map(user => ({
      value: user.id,
      label: `${user.name} (${user.role === 2 ? 'Technician' : 'Admin'})`
    }));

  // Filter tickets based on selected statuses and categories
  const filteredTickets = tickets.filter(ticket => {
    const statusMatch = statusFilters.length === 0 || statusFilters.includes(ticket.status);
    const categoryMatch = categoryFilters.length === 0 || categoryFilters.includes(ticket.categoryId);
    
    // Handle empty strings and null values for userId and technicianId
    const ticketUserId = ticket.userId && ticket.userId.trim() !== '' ? ticket.userId : null;
    const ticketTechnicianId = ticket.technicianId && ticket.technicianId.trim() !== '' ? ticket.technicianId : null;
    
    // Ensure boolean results for filtering - convert null to false
    const creatorMatch = creatorFilters.length === 0 || Boolean(ticketUserId && creatorFilters.includes(ticketUserId));
    const technicianMatch = technicianFilters.length === 0 || Boolean(ticketTechnicianId && technicianFilters.includes(ticketTechnicianId));
    
    return statusMatch && categoryMatch && creatorMatch && technicianMatch;
  });



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadTickets}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Technician View</h1>
          <p className="text-gray-600">Manage your assigned tickets</p>
        </div>
        <button
          onClick={loadTickets}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        {teams.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Queue</label>
            <select
              value={selectedTeamId ?? ''}
              onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Teams</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <MultiSelect
              options={statusOptions}
              selectedValues={statusFilters}
              onChange={setStatusFilters}
              placeholder="Select statuses..."
              label="Filter by Status"
            />
          </div>
          <div>
            <MultiSelect
              options={categoryOptions}
              selectedValues={categoryFilters}
              onChange={setCategoryFilters}
              placeholder="Select categories..."
              label="Filter by Category"
            />
          </div>
          <div>
            <MultiSelect
              options={creatorOptions}
              selectedValues={creatorFilters}
              onChange={setCreatorFilters}
              placeholder="Select creators..."
              label="Filter by Creator"
            />
          </div>
          <div>
            <MultiSelect
              options={technicianOptions}
              selectedValues={technicianFilters}
              onChange={setTechnicianFilters}
              placeholder="Select technicians..."
              label="Filter by Technician/Admin"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={loadTickets}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">All Tickets</h2>
        </div>
        
        {filteredTickets.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              {tickets.length === 0 
                ? "No tickets found. " 
                : "No tickets match your current filters. "
              }
              <Link to="/new-ticket" className="text-blue-600 hover:text-blue-700">Create a ticket</Link>
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTickets.map((ticket) => (
              <div key={ticket.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getStatusIcon(ticket.status)}
                      <span className="text-sm font-medium text-gray-900">
                        {getStatusText(ticket.status)}
                      </span>
                      {ticket.isSlaBreached && (
                        <XCircleIcon className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    
                    <Link
                      to={`/ticket/${ticket.id}`}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      {ticket.title}
                    </Link>
                    
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {ticket.userName}
                      </div>
                      <div>{formattedDates[ticket.id]}</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end space-y-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                      {getPriorityText(ticket.priority)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {ticket.categoryName}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicianView; 